import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useToasts } from "../../contexts/toasts";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Col,
  Container,
  Modal,
  Row,
} from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import {
  ColorPicker,
  Form,
  Input,
  Select,
  Tag,
  Tooltip,
  Upload,
  UploadProps,
} from "antd";
import { TailSpin } from "react-loader-spinner";
import dayjs from "dayjs";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faExclamation,
  faInfoCircle,
  faPaperPlane,
  faPen,
  faPrint,
  faRightLeft,
  faRotate,
  faStop,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import { Option } from "../../models/generics";
import { getSportName } from "../../utils/stringUtil";
import {
  FootballTeam,
  Pair,
  Tournament,
  TournamentFormat,
} from "../../types/Tournaments";
import { AdminFootballTeams, BracketMatch, PartialPair, Tournaments } from "../../api/Tournaments";
import { Structures } from "../../api/Structure";
import { UserFavorites } from "../../types/Structure";
import { Pairs } from "../../api/Pairs";
import BracketItem from "../../Components/BracketItem";
import FootballRosterEditor from "../../Components/FootballRosterEditor";
import { PDFDownloadLink } from "@react-pdf/renderer";
import TournamentPDF from "./TournamentPdf";

/**
 * Etichetta di un posto della coppia: un posto è di un utente registrato
 * oppure di un ospite (nome libero salvato sulla coppia), mai di entrambi.
 */
const slotLabel = (pair: Pair, slot: 1 | 2) => {
  const user = slot === 1 ? pair.player1 : pair.player2;
  if (user) return `${user.name} ${user.familyName}`;
  return ((slot === 1 ? pair.player1Name : pair.player2Name) || "").trim();
};

const isGuestSlot = (pair: Pair, slot: 1 | 2) =>
  !(slot === 1 ? pair.player1 : pair.player2) &&
  !!(slot === 1 ? pair.player1Name : pair.player2Name)?.trim();

export default function TournamentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { showToast } = useToasts();

  const [tournamentForm] = Form.useForm();

  const [tournament, setTournament] = useState<Tournament>();
  const [pairs, setPairs] = useState<Pair[]>();
  const [footballTeams, setFootballTeams] = useState<FootballTeam[]>();
  const [error, setError] = useState(false);
  const [fileList, setFileList] = useState<UploadProps["fileList"]>();
  const [usersList, setUsersList] = useState<Option[]>([]);
  const [favorites, setFavorites] = useState<UserFavorites[]>([]);
  const [endLoading, setEndLoading] = useState(false);
  const [refreshNamesLoading, setRefreshNamesLoading] = useState(false);
  const [remapModalOpen, setRemapModalOpen] = useState(false);
  const [remapFrom, setRemapFrom] = useState<string | undefined>();
  const [remapTo, setRemapTo] = useState<string | undefined>();
  const [remapLoading, setRemapLoading] = useState(false);
  const [teamToDelete, setTeamToDelete] = useState<FootballTeam>();
  const [organizerIds, setOrganizerIds] = useState<string[]>([]);
  // Lista squadre/coppie collassabile nel dettaglio torneo.
  const [participantsCollapsed, setParticipantsCollapsed] = useState(false);
  const [savingOrganizers, setSavingOrganizers] = useState(false);
  // Notifica ai partecipanti (titolo + testo).
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifyTitle, setNotifyTitle] = useState("");
  const [notifyText, setNotifyText] = useState("");
  const [notifyLoading, setNotifyLoading] = useState(false);
  // Iscrizione manuale di una coppia (o di un singolo, nei tornei americano):
  // ogni posto è un utente GetMove oppure un ospite indicato per nome.
  const [newPlayer1Id, setNewPlayer1Id] = useState<string | undefined>();
  const [newPlayer2Id, setNewPlayer2Id] = useState<string | undefined>();
  const [newGuest1, setNewGuest1] = useState("");
  const [newGuest2, setNewGuest2] = useState("");
  const [addingPair, setAddingPair] = useState(false);
  const componentRef = useRef<HTMLDivElement>(null);

  // Sport a squadre (calcio e basket): squadre con rose al posto delle coppie.
  const isFootball =
    tournament?.sport?.name === "Football" ||
    tournament?.sport?.name === "Basketball";
  // Nei tornei americano si iscrivono giocatori singoli: la coppia ha il solo
  // primo posto occupato.
  const isAmericano = tournament?.format === TournamentFormat.Americano;
  const isInCorso = tournament
    ? new Date(tournament.date) <= new Date() && !tournament.ended
    : false;

  const initFavorites = useCallback(() => {
    Structures.getHostFavorites().then(
      ({ data }) => {
        setFavorites(data.list);
        setUsersList(
          data.list.map((user) => ({
            label: `${user.user.name} ${user.user.familyName}`,
            value: user.userId,
          }))
        );
      },
      () => {
        showToast(
          "players-error",
          "Impossibile ottenere la lista dei giocatori",
          "",
          "danger"
        );
        setUsersList([]);
      }
    );
  }, [showToast]);

  const retrieveTournamentPairs = useCallback(() => {
    Pairs.getTournamentPairs(tournament!.id!).then(
      ({ data }) => {
        setPairs(data.list);
      },
      () => {
        showToast(
          "pairs-error",
          "Errore nel reperimento delle coppie per questo torneo",
          "",
          "danger"
        );
        setPairs([]);
      }
    );
  }, [tournament, showToast]);

  /**
   * Iscrive manualmente una coppia (o un giocatore singolo, nei tornei
   * americano). Ogni posto può essere un utente GetMove scelto tra i favoriti
   * del circolo oppure un ospite indicato per nome: se c'è l'utente, il nome
   * libero viene ignorato.
   */
  const handleAddPair = useCallback(() => {
    if (!tournament) return;
    const guest1 = newGuest1.trim();
    const guest2 = newGuest2.trim();
    if (!newPlayer1Id && !guest1) return;

    setAddingPair(true);
    Pairs.createPair({
      tournamentId: tournament.id!,
      type: tournament.type,
      player1Id: newPlayer1Id,
      player1Name: newPlayer1Id ? undefined : guest1,
      player2Id: isAmericano ? undefined : newPlayer2Id,
      player2Name:
        isAmericano || newPlayer2Id ? undefined : guest2 || undefined,
    }).then(
      () => {
        setAddingPair(false);
        setNewPlayer1Id(undefined);
        setNewPlayer2Id(undefined);
        setNewGuest1("");
        setNewGuest2("");
        showToast(
          "pair-created",
          isAmericano ? "Giocatore iscritto" : "Coppia iscritta",
          "",
          "success"
        );
        retrieveTournamentPairs();
      },
      () => {
        setAddingPair(false);
        showToast(
          "pair-create-error",
          "Errore nell'iscrizione al torneo",
          "",
          "danger"
        );
      }
    );
  }, [
    tournament,
    isAmericano,
    newPlayer1Id,
    newPlayer2Id,
    newGuest1,
    newGuest2,
    showToast,
    retrieveTournamentPairs,
  ]);

  const retrieveTournament = useCallback(() => {
    Tournaments.getTournament(id!).then(
      ({ data }) => {
        if (data.tournament.imageUrl) {
          Tournaments.getTournamentImage(data.tournament.id!).then(
            ({ data: imageData }) => {
              setFileList([
                {
                  uid: data.tournament.id!,
                  name: imageData.url.split("/")[4].split("?")[0],
                  status: "done",
                  url: imageData.url,
                },
              ]);
            },
            () => {
              showToast(
                "tournament-image-error",
                "Errore nel reperimento del Logo del Torneo",
                "",
                "danger"
              );
            }
          );
        }

        tournamentForm.setFieldsValue({
          ...data.tournament,
          date: data.tournament.date ? dayjs(data.tournament.date) : null,
          registrationDeadline: data.tournament.registrationDeadline
            ? dayjs(data.tournament.registrationDeadline)
            : null,
        });
        setTournament(data.tournament);
        setOrganizerIds(
          (data.tournament.organizers || []).map((o) => o.userId)
        );
      },
      () => {
        showToast(
          "facility-error",
          "Errore nel reperimento del Campo",
          "",
          "danger"
        );
        setError(true);
      }
    );
  }, [showToast, tournamentForm, id]);

  const handleSaveOrganizers = useCallback(() => {
    setSavingOrganizers(true);
    const organizers = organizerIds
      .map((uid) => favorites.find((f) => f.userId === uid))
      .filter(Boolean)
      .map((f) => ({
        userId: f!.userId,
        name: f!.user.name,
        familyName: f!.user.familyName,
        email: f!.user.email,
      }));
    Tournaments.setOrganizers(id!, organizers).then(
      () => {
        setSavingOrganizers(false);
        showToast(
          "organizers-success",
          "Organizzatori aggiornati",
          "",
          "success"
        );
        retrieveTournament();
      },
      () => {
        setSavingOrganizers(false);
        showToast(
          "organizers-error",
          "Impossibile aggiornare gli organizzatori",
          "",
          "danger"
        );
      }
    );
  }, [organizerIds, favorites, id, showToast, retrieveTournament]);

  const retrieveFootballTeams = useCallback(() => {
    AdminFootballTeams.getTeams(tournament!.id!).then(
      ({ data }) => setFootballTeams(data.teams),
      () => {
        showToast("teams-error", "Errore nel reperimento delle squadre", "", "danger");
        setFootballTeams([]);
      }
    );
  }, [tournament, showToast]);

  const handleEndTournament = useCallback(() => {
    setEndLoading(true);
    Tournaments.endTournament(tournament!.id!).then(
      () => {
        setEndLoading(false);
        showToast("end-tournament-success", "Torneo terminato con successo", "", "success");
        retrieveTournament();
      },
      () => {
        setEndLoading(false);
        showToast("end-tournament-error", "Errore nella chiusura del torneo", "", "danger");
      }
    );
  }, [tournament, showToast, retrieveTournament]);

  const openNotifyModal = useCallback(() => {
    setNotifyTitle("");
    setNotifyText("");
    setNotifyOpen(true);
  }, []);

  const handleSendNotification = useCallback(() => {
    const title = notifyTitle.trim();
    const text = notifyText.trim();
    if (!title || !text) {
      showToast(
        "notify-validation",
        "Inserisci sia il titolo che il testo della notifica",
        "",
        "danger"
      );
      return;
    }
    setNotifyLoading(true);
    Tournaments.notifyParticipants(tournament!.id!, { title, text }).then(
      ({ data }) => {
        setNotifyLoading(false);
        setNotifyOpen(false);
        showToast(
          "notify-success",
          `Notifica inviata a ${data.recipients} iscritt${
            data.recipients === 1 ? "o" : "i"
          }`,
          "",
          "success"
        );
      },
      () => {
        setNotifyLoading(false);
        showToast(
          "notify-error",
          "Errore nell'invio della notifica ai partecipanti",
          "",
          "danger"
        );
      }
    );
  }, [notifyTitle, notifyText, tournament, showToast]);

  // Voci distinte presenti nel tabellone, con il nome così com'è salvato lì
  // dentro. Serve per la rimappatura: sono queste le voci che si possono
  // puntare su una coppia/squadra. Escludiamo i segnaposto `Winner-*`, che non
  // sono partecipanti ma slot calcolati.
  const bracketEntries = useMemo(() => {
    const bracket = tournament?.tournamentBracket;
    if (!bracket) return [];

    const byId = new Map<string, string>();
    const addPair = (pair: any) => {
      if (!pair || pair === "BYE" || !pair.id) return;
      if (String(pair.id).startsWith("Winner-")) return;
      if (byId.has(pair.id)) return;
      const label = [pair.player1Name, pair.player2Name]
        .filter(Boolean)
        .join(" / ");
      byId.set(pair.id, label || "(senza nome)");
    };
    const addRounds = (rounds: any[] | undefined) =>
      (rounds ?? []).forEach((round) =>
        (round.matches ?? []).forEach((match: any) => {
          addPair(match.home);
          addPair(match.away);
        })
      );

    addRounds(bracket.goldBracket);
    addRounds(bracket.silverBracket);
    addRounds(bracket.rounds);
    (bracket.roundRobin?.gironi ?? []).forEach((girone: any) => {
      (girone.pairs ?? []).forEach(addPair);
      addRounds(
        (girone.calendario ?? []).map((day: any) => ({ matches: day.matches }))
      );
    });

    return Array.from(byId, ([id, label]) => ({ id, label }));
  }, [tournament]);

  // Squadre/coppie reali su cui si può puntare una voce, con lo stesso formato
  // dei nomi usato nel tabellone.
  const remapTargets = useMemo(() => {
    if (isFootball) {
      return (footballTeams ?? []).map((team) => ({
        id: team.id,
        label: team.name,
      }));
    }
    return (pairs ?? []).map((pair) => ({
      id: pair.id,
      label:
        [slotLabel(pair, 1), slotLabel(pair, 2)].filter(Boolean).join(" / ") ||
        "(coppia senza giocatori)",
    }));
  }, [isFootball, footballTeams, pairs]);

  const handleRemapEntry = useCallback(() => {
    if (!remapFrom || !remapTo) return;
    setRemapLoading(true);
    Tournaments.remapBracketEntry(tournament!.id!, remapFrom, remapTo).then(
      ({ data }) => {
        setRemapLoading(false);
        setRemapModalOpen(false);
        setRemapFrom(undefined);
        setRemapTo(undefined);
        showToast(
          "remap-entry-success",
          data.replaced > 0
            ? `Voce rimappata in ${data.replaced} ${
                data.replaced === 1 ? "posizione" : "posizioni"
              } del tabellone`
            : "Nessuna posizione da aggiornare",
          "",
          "success"
        );
        retrieveTournament();
      },
      () => {
        setRemapLoading(false);
        showToast(
          "remap-entry-error",
          "Errore nella rimappatura della voce",
          "",
          "danger"
        );
      }
    );
  }, [tournament, remapFrom, remapTo, showToast, retrieveTournament]);

  // I nomi sono copiati dentro il tabellone quando lo si genera, quindi
  // rinominare una squadra o cambiare i giocatori di una coppia non si propaga
  // da solo: questo li riallinea senza toccare risultati, marcatori e orari.
  const handleRefreshTeamNames = useCallback(() => {
    setRefreshNamesLoading(true);
    Tournaments.refreshBracketNames(tournament!.id!).then(
      ({ data }) => {
        setRefreshNamesLoading(false);
        const entita = isFootball
          ? data.renamed === 1
            ? "squadra aggiornata"
            : "squadre aggiornate"
          : data.renamed === 1
          ? "coppia aggiornata"
          : "coppie aggiornate";
        showToast(
          "refresh-names-success",
          data.renamed > 0
            ? `Tabellone aggiornato: ${data.renamed} ${entita}`
            : "Tabellone già aggiornato: nessun nome da correggere",
          "",
          "success"
        );
        retrieveTournament();
      },
      () => {
        setRefreshNamesLoading(false);
        showToast(
          "refresh-names-error",
          "Errore nell'aggiornamento del tabellone",
          "",
          "danger"
        );
      }
    );
  }, [tournament, isFootball, showToast, retrieveTournament]);

  const handleDeleteTeam = useCallback((team: FootballTeam) => {
    AdminFootballTeams.deleteTeam(team.id).then(
      () => {
        showToast("delete-team-success", `Squadra "${team.name}" eliminata`, "", "success");
        setTeamToDelete(undefined);
        retrieveFootballTeams();
      },
      () => showToast("delete-team-error", "Errore nell'eliminazione della squadra", "", "danger")
    );
  }, [showToast, retrieveFootballTeams]);

  useEffect(() => {
    if (id && tournament && !tournament.fitp) {
      if (isFootball) {
        retrieveFootballTeams();
      } else {
        retrieveTournamentPairs();
      }
    }
  }, [id, tournament, isFootball, retrieveTournamentPairs, retrieveFootballTeams]);

  useEffect(() => {
    initFavorites();
    if (id) retrieveTournament();
    else navigate("/tornei");
  }, [retrieveTournament, id, tournamentForm, navigate, initFavorites]);

  // const handleBracketModalClose = useCallback(
  //   () => setShowBracketsModal(false),
  //   []
  // );

  const renderPairs = useCallback(() => {
    if (!tournament)
      return (
        <div className="d-flex justify-content-center py-5 my-5">
          <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
        </div>
      );
    if (!pairs)
      return (
        <div className="d-flex justify-content-center py-5 my-5">
          {tournament.fitp ? (
            <h6 className="text-warning">Torneo gestito dalla FITP</h6>
          ) : (
            <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
          )}
        </div>
      );

    // Chi è già iscritto non va riproposto nella tendina.
    const takenIds = new Set(
      pairs
        .flatMap((pair) => [pair.player1Id, pair.player2Id])
        .filter(Boolean) as string[]
    );
    const freeUsers = usersList.filter(
      (user) => !takenIds.has(String(user.value))
    );
    const canSubmit = !!newPlayer1Id || newGuest1.trim().length >= 2;

    const renderSlot = (pair: Pair, slot: 1 | 2) => {
      const label = slotLabel(pair, slot);
      return (
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: 6,
            minWidth: 0,
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            className={label ? undefined : "text-muted"}
          >
            {label || "Posto libero"}
          </span>
          {isGuestSlot(pair, slot) && (
            <Tag color="purple" style={{ marginInlineEnd: 0 }}>
              Ospite
            </Tag>
          )}
        </div>
      );
    };

    return (
      <Card.Body>
        {pairs.length === 0 ? (
          <small className="text-muted">Nessuna iscrizione</small>
        ) : (
          pairs.map((pair, index) => (
            <div
              key={pair.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "6px 0",
                borderBottom: "1px solid #f5f5f5",
              }}
            >
              <span style={{ width: 20, color: "#aaa", fontSize: 12 }}>
                {index + 1}
              </span>
              {renderSlot(pair, 1)}
              {!isAmericano && (
                <>
                  <span style={{ color: "#ccc" }}>/</span>
                  {renderSlot(pair, 2)}
                </>
              )}
            </div>
          ))
        )}

        {/* Iscrizione manuale: utente GetMove oppure ospite indicato per nome */}
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 8 }}>
            {isAmericano ? "Iscrivi un giocatore" : "Iscrivi una coppia"}
          </div>

          <Row className="g-2 mb-2">
            <Col xs={12} md={6}>
              <Select
                showSearch
                allowClear
                style={{ width: "100%" }}
                placeholder={
                  isAmericano ? "Utente GetMove" : "Giocatore 1 (utente GetMove)"
                }
                value={newPlayer1Id}
                options={freeUsers}
                optionFilterProp="label"
                disabled={addingPair}
                notFoundContent="Nessun utente disponibile"
                onChange={(value) => {
                  setNewPlayer1Id(value);
                  if (value) setNewGuest1("");
                }}
              />
            </Col>
            <Col xs={12} md={6}>
              <Input
                placeholder="oppure nome ospite"
                value={newGuest1}
                disabled={addingPair || !!newPlayer1Id}
                onChange={(e) => setNewGuest1(e.target.value)}
                onPressEnter={handleAddPair}
              />
            </Col>
          </Row>

          {!isAmericano && (
            <Row className="g-2 mb-2">
              <Col xs={12} md={6}>
                <Select
                  showSearch
                  allowClear
                  style={{ width: "100%" }}
                  placeholder="Giocatore 2 (utente GetMove)"
                  value={newPlayer2Id}
                  options={freeUsers.filter(
                    (user) => user.value !== newPlayer1Id
                  )}
                  optionFilterProp="label"
                  disabled={addingPair}
                  notFoundContent="Nessun utente disponibile"
                  onChange={(value) => {
                    setNewPlayer2Id(value);
                    if (value) setNewGuest2("");
                  }}
                />
              </Col>
              <Col xs={12} md={6}>
                <Input
                  placeholder="oppure nome ospite"
                  value={newGuest2}
                  disabled={addingPair || !!newPlayer2Id}
                  onChange={(e) => setNewGuest2(e.target.value)}
                  onPressEnter={handleAddPair}
                />
              </Col>
            </Row>
          )}

          <div className="d-flex justify-content-between align-items-center">
            <small className="text-muted">
              Gli ospiti non hanno un profilo GetMove: compaiono nel tabellone
              solo con il nome.
            </small>
            <Button
              size="sm"
              variant="primary"
              disabled={!canSubmit || addingPair}
              onClick={handleAddPair}
            >
              {addingPair ? "Iscrizione…" : "Aggiungi"}
            </Button>
          </div>
        </div>
      </Card.Body>
    );
  }, [
    pairs,
    tournament,
    usersList,
    isAmericano,
    newPlayer1Id,
    newPlayer2Id,
    newGuest1,
    newGuest2,
    addingPair,
    handleAddPair,
  ]);

  const renderFootballTeams = useCallback(() => {
    if (!tournament) return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={60} width={60} color="#f25d0b" radius="1" />
      </div>
    );
    if (!footballTeams) return (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={60} width={60} color="#f25d0b" radius="1" />
      </div>
    );
    if (footballTeams.length === 0) return (
      <Card.Body>
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span className="ms-2">Nessuna squadra iscritta</span>
        </Alert>
      </Card.Body>
    );
    return (
      <Card.Body>
        <FootballRosterEditor
          teams={footballTeams}
          favorites={favorites}
          onChanged={retrieveFootballTeams}
          onDeleteTeam={setTeamToDelete}
        />
      </Card.Body>
    );
  }, [tournament, footballTeams, favorites, retrieveFootballTeams]);

  const renderTournamentForm = useCallback(() => {
    const t = tournament!;
    const infoRow = (label: string, value: React.ReactNode) => (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          padding: "10px 0",
          borderBottom: "1px solid #f5f5f5",
        }}
      >
        <small style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5 }}>
          {label}
        </small>
        <span style={{ fontSize: 14, fontWeight: 500, color: "#222" }}>{value || "—"}</span>
      </div>
    );

    const typeLabel = { Men: "Maschile", Women: "Femminile", Mixed: "Misto" }[t.type] ?? t.type;

    return (
      <Card.Body style={{ padding: "16px 20px" }}>
        <Row>
          <Col xs={12} md={6}>
            {infoRow("Sport", getSportName(t.sport?.name || ""))}
            {infoRow("Tipologia", typeLabel)}
            {infoRow("Data torneo", new Date(t.date).toLocaleDateString("it", { day: "2-digit", month: "long", year: "numeric" }))}
            {infoRow("Scadenza iscrizioni", new Date(t.registrationDeadline).toLocaleDateString("it", { day: "2-digit", month: "long", year: "numeric" }))}
          </Col>
          <Col xs={12} md={6}>
            {infoRow("Torneo FITP", t.fitp ? "Sì" : "No")}
            {t.fitp && t.link && infoRow("Link FITP", <a href={t.link} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>{t.link}</a>)}
            {!t.fitp && infoRow(isFootball ? "Max squadre" : "Max coppie", t.maxPairs?.toString())}
            {isFootball && t.maxPlayersPerTeam && infoRow("Giocatori per squadra", t.maxPlayersPerTeam.toString())}
            {infoRow("Colore principale", <ColorPicker value={t.mainColor || "#f25d0b"} showText disabled />)}
          </Col>
        </Row>
        {t.description && (
          <div style={{ marginTop: 8, padding: "10px 0", borderTop: "1px solid #f5f5f5" }}>
            <small style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5 }}>
              Descrizione
            </small>
            <p style={{ fontSize: 13, color: "#444", marginTop: 4, marginBottom: 0 }}>{t.description}</p>
          </div>
        )}
        {fileList && fileList.length > 0 && (
          <div style={{ marginTop: 8, paddingTop: 10, borderTop: "1px solid #f5f5f5" }}>
            <small style={{ color: "#888", fontWeight: 600, textTransform: "uppercase", fontSize: 10, letterSpacing: 0.5, display: "block", marginBottom: 8 }}>
              Locandina
            </small>
            <Upload
              disabled
              fileList={fileList}
              listType="picture"
              showUploadList={{ showDownloadIcon: true }}
              beforeUpload={() => false}
            />
          </div>
        )}
      </Card.Body>
    );
  }, [fileList, tournament, isFootball]);

  const renderAsyncContent = useCallback(() => {
    if (error)
      return (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={faExclamation} />
          <span className="ms-2">
            Errore nel reperimento dei dati, per favore riprova.
          </span>
        </Alert>
      );

    return tournament ? (
      renderTournamentForm()
    ) : (
      <div className="d-flex justify-content-center py-5 my-5">
        <TailSpin height={100} width={100} color="#f25d0b" radius="1" />
      </div>
    );
  }, [error, tournament, renderTournamentForm]);

  const parseGoldBracket = useMemo(() => {
    if (
      !tournament ||
      !tournament.tournamentBracket ||
      !tournament.tournamentBracket.goldBracket
    )
      return [];
    const brackets = tournament.tournamentBracket;
    return (brackets!.goldBracket || []).map((round) => ({
      title: round.name,
      seeds: round.matches.map((m) => ({
        id: `${m.home !== "BYE" ? m.home?.id : "bye"}-${
          m.away !== "BYE" ? m.away?.id : "bye"
        }`,
        teams: [
          {
            name:
              m.home === "BYE"
                ? "BYE"
                : `${m.home.player1Name} ${
                    m.home.player2Name ? ` / ${m.home.player2Name}` : ""
                  }`,
          },
          {
            name:
              m.away === "BYE"
                ? "BYE"
                : `${m.away.player1Name} ${
                    m.away.player2Name ? ` / ${m.away.player2Name}` : ""
                  }`,
          },
        ],
        live: m.live,
        dateTime: m.dateTime,
      })),
    }));
  }, [tournament]);

  const parseSilverBracket = useMemo(() => {
    if (
      !tournament ||
      !tournament.tournamentBracket ||
      !tournament.tournamentBracket.silverBracket
    )
      return [];
    const brackets = tournament.tournamentBracket;
    return brackets!.silverBracket?.map((round) => ({
      title: round.name,
      seeds: round.matches.map((m) => ({
        id: m.id,
        teams: [
          {
            name:
              m.home === "BYE"
                ? "BYE"
                : `${m.home.player1Name} ${
                    m.home.player2Name ? ` / ${m.home.player2Name}` : ""
                  }`,
          },
          {
            name:
              m.away === "BYE"
                ? "BYE"
                : `${m.away.player1Name} ${
                    m.away.player2Name ? ` / ${m.away.player2Name}` : ""
                  }`,
          },
        ],
        live: m.live,
        dateTime: m.dateTime,
      })),
    }));
  }, [tournament]);

  const parseDirectBracket = useMemo(() => {
    if (
      !tournament ||
      !tournament.tournamentBracket ||
      !tournament.tournamentBracket.rounds
    )
      return;
    const brackets = tournament.tournamentBracket;
    return brackets.rounds?.map((round) => ({
      title: round.name,
      seeds: round.matches.map((m) => ({
        id: `${m.home !== "BYE" ? m.home?.id : "bye"}-${
          m.away !== "BYE" ? m.away?.id : "bye"
        }`,
        teams: [
          {
            name:
              m.home === "BYE"
                ? "BYE"
                : typeof m.home === "string"
                ? m.home
                : `${m.home.player1Name} ${
                    m.home.player2Name ? ` / ${m.home.player2Name}` : ""
                  }`,
          },
          {
            name:
              m.away === "BYE"
                ? "BYE"
                : typeof m.away === "string"
                ? m.away
                : `${m.away.player1Name} ${
                    m.away.player2Name ? ` / ${m.away.player2Name}` : ""
                  }`,
          },
        ],
        live: m.live,
        dateTime: m.dateTime,
      })),
    }));
  }, [tournament]);

  const renderTabellone = useCallback(() => {
    if (!tournament || !tournament.tournamentBracket) return;
    const brackets = tournament.tournamentBracket;
    if (!brackets.rounds || brackets.rounds.length === 0)
      return (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span className="ms-2">Nessun tabellone</span>
        </Alert>
      );
    return (
      <Row className="mb-3 justify-content-center w-100">
        <BracketItem rounds={parseDirectBracket || []} />
      </Row>
    );
  }, [tournament, parseDirectBracket]);

  const isRiposoMatch = useCallback((fixture: BracketMatch[]) => {
    let restMatch: string = "";
    fixture.forEach((match) => {
      if (match.home !== "BYE") {
        //Controllo match.away
        if (match.away !== "BYE") {
          if (
            match.away.player1Name === "Riposo" ||
            match.away.player2Name === "Riposo"
          )
            restMatch = `${match.home.player1Name} / ${match.home.player2Name}`;
          else if (
            match.home.player1Name === "Riposo" ||
            match.home.player2Name === "Riposo"
          )
            restMatch = `${match.away.player1Name} / ${match.away.player2Name}`;
        }
      }
    });
    return restMatch;
  }, []);

  const renderGironi = useCallback(() => {
    if (!tournament || !tournament.tournamentBracket) return;
    const brackets = tournament.tournamentBracket;
    if (!brackets.gironi || brackets.gironi.length === 0)
      return (
        <Alert variant="info" className="py-2">
          <FontAwesomeIcon icon={faInfoCircle} />
          <span className="ms-2">Nessun girone</span>
        </Alert>
      );
    return (
      <div ref={componentRef}>
        <div className="my-3 justify-content-center align-items-center text-center">
          <img
            src={require("../../assets/logo/logo.png")}
            alt="Get Move Logo"
            width={150}
            height={100}
          />
        </div>
        <h5 className="text-center">{tournament.name}</h5>
        <Row className="my-3 justify-content-center mx-auto text-center w-100">
          <h5 className="text-center mb-3">Gironi:</h5>
          {(brackets.roundRobin?.gironi || []).map((girone) => (
            <Col
              xs={12}
              sm={6}
              md={4}
              lg={3}
              key={girone.name}
              className="mb-4"
            >
              <Card className="shadow-sm h-100">
                <Card.Header className="text-center fw-bold fs-5">
                  Girone {girone.name}
                </Card.Header>
                <Card.Body>
                  {girone.pairs.map((pair, idx) => (
                    <div
                      key={pair.id}
                      className="d-flex justify-content-between py-2"
                    >
                      <small className="ms-2 text-center flex-grow-1">
                        {pair.player1Name} / {pair.player2Name}
                      </small>
                    </div>
                  ))}
                  {girone.pairs.length === 0 && (
                    <div className="text-center text-muted">Nessuna coppia</div>
                  )}
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        <div className="mb-4">
          {(brackets.roundRobin?.gironi || []).map((girone, index) => (
            <Row key={index} className="my-4">
              <h5 className="text-start">Girone {girone.name}:</h5>
              {(girone.calendario || []).map((fixture, i) => (
                <Row
                  key={i}
                  className="justify-content-center text-start mx-auto my-3 align-items-center"
                >
                  <h6>{fixture.giornata}° Giornata:</h6>
                  <Row className="text-start justify-content-start">
                    {fixture.matches
                      .filter(
                        (match) =>
                          (match.home as PartialPair).id !== "BYE" &&
                          (match.away as PartialPair).id !== "BYE"
                      )
                      .map((match, idx) => (
                        <Row
                          className="text-start justify-content-center"
                          key={idx}
                        >
                          {(match.home as PartialPair).id !== "BYE" &&
                            (match.away as PartialPair).id !== "BYE" && (
                              <small>
                                {`${
                                  (match.home as PartialPair).player1Name
                                } / ${(match.home as PartialPair).player2Name}`}
                                {" "}-{" "}
                                {`${
                                  (match.away as PartialPair).player1Name
                                } / ${(match.away as PartialPair).player2Name}`}
                              </small>
                            )}
                        </Row>
                      ))}
                    <small>
                      {isRiposoMatch(fixture.matches) !== ""
                        ? `Riposa: ${isRiposoMatch(fixture.matches)}`
                        : ""}
                    </small>
                  </Row>
                </Row>
              ))}
            </Row>
          ))}
        </div>
        <div className="mb-3 justify-content-center w-100">
          <h3 className="text-center">
            Fase Finale {brackets.silverBracket ? "Gold" : ""}
          </h3>
          <BracketItem rounds={parseGoldBracket} />
        </div>
        {brackets.silverBracket && (
          <Row className="mb-3 justify-content-center w-100">
            <h3 className="text-center">Tabellone Silver</h3>
            <BracketItem rounds={parseSilverBracket || []} />
          </Row>
        )}
      </div>
    );
  }, [parseGoldBracket, parseSilverBracket, tournament, isRiposoMatch]);

  return (
    <Container className="my-3">
      <Helmet>
        <title>{tournament?.name}</title>
      </Helmet>

      {/* Breadcrumb */}
      <Breadcrumb className="mb-3">
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: "/tornei" }}>
          Tornei
        </Breadcrumb.Item>
        {tournament ? (
          <Breadcrumb.Item active>{tournament.name}</Breadcrumb.Item>
        ) : (
          <Breadcrumb.Item active>
            <TailSpin color="#f25d0b" width={16} height={16} />
          </Breadcrumb.Item>
        )}
      </Breadcrumb>

      {/* Page header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
          marginBottom: 20,
          padding: "16px 20px",
          background: "#fff",
          borderRadius: 12,
          border: "1px solid #e8e8e8",
          boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <h5 style={{ margin: 0, fontWeight: 700 }}>
            {tournament?.name ?? <TailSpin color="#f25d0b" width={18} height={18} />}
          </h5>
          {tournament && (
            <Badge
              bg={tournament.ended ? "danger" : isInCorso ? "warning" : "success"}
              style={{ fontSize: 11 }}
            >
              {tournament.ended ? "Terminato" : isInCorso ? "In corso" : "Prossimo"}
            </Badge>
          )}
        </div>

        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {isInCorso && (
            <Button variant="danger" size="sm" disabled={endLoading} onClick={handleEndTournament}>
              {endLoading ? (
                <TailSpin color="#FFF" width={14} height={14} />
              ) : (
                <>
                  <FontAwesomeIcon icon={faStop} className="me-1" />
                  Termina Torneo
                </>
              )}
            </Button>
          )}
          <Button variant="outline-success" size="sm" onClick={openNotifyModal}>
            <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
            Invia notifica ai partecipanti
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={() => navigate("generate-schedule")}>
            Genera Tabelloni
          </Button>
          {tournament?.tournamentBracket && (
            <Button variant="outline-primary" size="sm" onClick={() => navigate("schedule")}>
              Vedi Tabellone
            </Button>
          )}
          {tournament?.tournamentBracket && (
            <Tooltip
              title={
                isFootball
                  ? "Riallinea i nomi delle squadre nel tabellone dopo una rinomina. Non modifica risultati né orari."
                  : "Riallinea i nomi dei giocatori nel tabellone dopo un cambio nelle coppie. Non modifica risultati né orari."
              }
              mouseEnterDelay={0.3}
            >
              <Button
                variant="outline-secondary"
                size="sm"
                disabled={refreshNamesLoading}
                onClick={handleRefreshTeamNames}
              >
                {refreshNamesLoading ? (
                  <TailSpin color="#6c757d" width={14} height={14} />
                ) : (
                  <>
                    <FontAwesomeIcon icon={faRotate} className="me-1" />
                    Aggiorna Tabellone
                  </>
                )}
              </Button>
            </Tooltip>
          )}
          {tournament?.tournamentBracket && (
            <Tooltip
              title={`Punta una voce del tabellone su ${
                isFootball ? "un'altra squadra" : "un'altra coppia"
              }, quando "Aggiorna Tabellone" non basta.`}
              mouseEnterDelay={0.3}
            >
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setRemapModalOpen(true)}
              >
                <FontAwesomeIcon icon={faRightLeft} className="me-1" />
                Rimappa Voce
              </Button>
            </Tooltip>
          )}
          <Tooltip title="Modifica torneo" mouseEnterDelay={0.3}>
            <Button
              variant="outline-dark"
              size="sm"
              disabled={!tournament}
              onClick={() => navigate("modifica")}
            >
              <FontAwesomeIcon icon={faPen} className="me-1" />
              Modifica
            </Button>
          </Tooltip>
        </div>
      </div>

      <Row className="g-3">
        <Col xs={12} md={6}>
          <Card style={{ border: "1px solid #e8e8e8", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <div
              style={{
                padding: "12px 20px",
                borderBottom: "1px solid #f0f0f0",
                background: "#fafafa",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 14 }}>Informazioni Torneo</span>
            </div>
            {renderAsyncContent()}
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card style={{ border: "1px solid #e8e8e8", borderRadius: 12, overflow: "hidden", boxShadow: "0 2px 8px rgba(0,0,0,0.04)" }}>
            <div
              onClick={() => setParticipantsCollapsed((prev) => !prev)}
              style={{
                padding: "12px 20px",
                borderBottom: participantsCollapsed ? "none" : "1px solid #f0f0f0",
                background: "#fafafa",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                cursor: "pointer",
                userSelect: "none",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 14 }}>{isFootball ? "Squadre Iscritte" : "Coppie Iscritte"}</span>
              <FontAwesomeIcon
                icon={participantsCollapsed ? faChevronDown : faChevronUp}
                style={{ fontSize: 12, color: "#888" }}
              />
            </div>
            {!participantsCollapsed && (isFootball ? renderFootballTeams() : renderPairs())}
          </Card>
        </Col>
      </Row>

      <Row className="g-3 mt-1">
        <Col xs={12}>
          <Card
            style={{
              border: "1px solid #e8e8e8",
              borderRadius: 12,
              overflow: "hidden",
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <div
              style={{
                padding: "12px 20px",
                borderBottom: "1px solid #f0f0f0",
                background: "#fafafa",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <span style={{ fontWeight: 700, fontSize: 14 }}>
                Organizzatori del torneo
              </span>
              <Button
                size="sm"
                variant="success"
                disabled={savingOrganizers}
                onClick={handleSaveOrganizers}
              >
                {savingOrganizers ? (
                  <TailSpin color="#fff" width={15} height={15} />
                ) : (
                  "Salva organizzatori"
                )}
              </Button>
            </div>
            <Card.Body>
              <p className="text-muted small mb-2">
                Gli organizzatori sono utenti registrati su GetMove che potranno
                gestire i risultati delle partite di questo torneo direttamente
                dall'app.
              </p>
              <Select
                mode="multiple"
                allowClear
                showSearch
                style={{ width: "100%" }}
                placeholder="Seleziona gli organizzatori"
                value={organizerIds}
                onChange={(vals: string[]) => setOrganizerIds(vals)}
                options={usersList}
                filterOption={(input, option) =>
                  (option?.label ?? "")
                    .toString()
                    .toLowerCase()
                    .includes(input.toLowerCase())
                }
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Confirm delete team modal */}
      <Modal show={notifyOpen} onHide={() => setNotifyOpen(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Invia notifica ai partecipanti</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 13, color: "#666", marginBottom: 16 }}>
            La notifica verrà inviata a tutti gli utenti GetMove iscritti a
            questo torneo{" "}
            {isFootball
              ? "(presidenti e giocatori registrati delle squadre)"
              : "(entrambi i giocatori di ogni coppia)"}
            .
          </p>
          <div style={{ marginBottom: 12 }}>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: "#888",
                marginBottom: 4,
              }}
            >
              Titolo
            </label>
            <Input
              value={notifyTitle}
              onChange={(e) => setNotifyTitle(e.target.value)}
              placeholder="Es. Aggiornamento orari"
              maxLength={80}
              showCount
            />
          </div>
          <div>
            <label
              style={{
                display: "block",
                fontSize: 12,
                fontWeight: 600,
                color: "#888",
                marginBottom: 4,
              }}
            >
              Testo
            </label>
            <Input.TextArea
              value={notifyText}
              onChange={(e) => setNotifyText(e.target.value)}
              placeholder="Scrivi il messaggio da inviare ai partecipanti…"
              rows={5}
              maxLength={500}
              showCount
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() => setNotifyOpen(false)}
            disabled={notifyLoading}
          >
            Annulla
          </Button>
          <Button
            variant="success"
            size="sm"
            onClick={handleSendNotification}
            disabled={
              notifyLoading || !notifyTitle.trim() || !notifyText.trim()
            }
          >
            {notifyLoading ? (
              <TailSpin color="#FFF" width={14} height={14} />
            ) : (
              <>
                <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                Invia notifica
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={remapModalOpen}
        onHide={() => setRemapModalOpen(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Rimappa voce del tabellone</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ fontSize: 13, color: "#666" }}>
            Punta una voce del tabellone su{" "}
            {isFootball ? "una squadra" : "una coppia"} diversa. Serve quando{" "}
            {isFootball ? "la squadra" : "la coppia"} è stata sostituita, o per i
            tabelloni vecchi in cui "Aggiorna Tabellone" non riesce ad
            agganciare la voce. Il posto nel tabellone non cambia: risultati e
            orari restano dove sono.
          </p>
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Voce nel tabellone
            </div>
            <Select
              style={{ width: "100%" }}
              placeholder="Seleziona la voce da sostituire"
              value={remapFrom}
              onChange={setRemapFrom}
              showSearch
              optionFilterProp="children"
            >
              {bracketEntries.map((entry) => (
                <Select.Option key={entry.id} value={entry.id}>
                  {entry.label}
                </Select.Option>
              ))}
            </Select>
          </div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
              Sostituisci con
            </div>
            <Select
              style={{ width: "100%" }}
              placeholder={
                isFootball ? "Seleziona la squadra" : "Seleziona la coppia"
              }
              value={remapTo}
              onChange={setRemapTo}
              showSearch
              optionFilterProp="children"
            >
              {remapTargets.map((target) => (
                <Select.Option key={target.id} value={target.id}>
                  {target.label}
                </Select.Option>
              ))}
            </Select>
          </div>
          {bracketEntries.length === 0 && (
            <Alert variant="warning" className="mt-3 mb-0" style={{ fontSize: 13 }}>
              Nessuna voce trovata nel tabellone.
            </Alert>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            onClick={() => setRemapModalOpen(false)}
          >
            Annulla
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={!remapFrom || !remapTo || remapLoading}
            onClick={handleRemapEntry}
          >
            {remapLoading ? (
              <TailSpin color="#FFF" width={14} height={14} />
            ) : (
              "Rimappa"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={!!teamToDelete} onHide={() => setTeamToDelete(undefined)}>
        <Modal.Header closeButton>
          <Modal.Title>Elimina Squadra</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Sei sicuro di voler eliminare la squadra <strong>{teamToDelete?.name}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" size="sm" className="border" onClick={() => setTeamToDelete(undefined)}>
            Annulla
          </Button>
          <Button variant="danger" size="sm" onClick={() => handleDeleteTeam(teamToDelete!)}>
            <FontAwesomeIcon icon={faTrash} className="me-1" />
            Elimina
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={false}
        size="xl"
        // onHide={handleBracketModalClose}
      >
        <Modal.Header closeButton>
          <Modal.Title>Tabellone Torneo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(tournament?.tournamentBracket?.gironi || []).length > 0
            ? renderGironi()
            : renderTabellone()}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            size="sm"
            className="border"
            // onClick={handleBracketModalClose}
          >
            Chiudi
          </Button>
          <PDFDownloadLink
            document={
              <TournamentPDF
                tournament={{
                  ...tournament,
                  name: tournament?.name
                    .replace(
                      /([#0-9]\u20E3)|[\xA9\xAE\u203C\u2047-\u2049\u2122\u2139\u3030\u303D\u3297\u3299][\uFE00-\uFEFF]?|[\u2190-\u21FF][\uFE00-\uFEFF]?|[\u2300-\u23FF][\uFE00-\uFEFF]?|[\u2460-\u24FF][\uFE00-\uFEFF]?|[\u25A0-\u25FF][\uFE00-\uFEFF]?|[\u2600-\u27BF][\uFE00-\uFEFF]?|[\u2900-\u297F][\uFE00-\uFEFF]?|[\u2B00-\u2BF0][\uFE00-\uFEFF]?|(?:\uD83C[\uDC00-\uDFFF]|\uD83D[\uDC00-\uDEFF])[\uFE00-\uFEFF]?|[\u20E3]|[\u26A0-\u3000]|\uD83E[\udd00-\uddff]|[\u00A0-\u269F]/g,
                      ""
                    )
                    .trim(),
                }}
                brackets={tournament?.tournamentBracket}
                parseGoldBracket={parseGoldBracket}
                parseSilverBracket={parseSilverBracket}
              ></TournamentPDF>
            }
            fileName={`${tournament?.name}.pdf`}
          >
            <Button variant="success" className="align-items-center" size="sm">
              <FontAwesomeIcon icon={faPrint} />
              <span className="ms-2">Stampa</span>
            </Button>
          </PDFDownloadLink>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
